import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Globe, Users, Compass, ArrowUpRight } from "lucide-react";
import { NAV_ITEMS } from "@/lib/nav";
import { useAuth } from "@/auth/AuthContext";
import api from "@/lib/api";
import { localizeLabel } from "@/lib/marketFormat";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";

export const CommandPalette = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { activeOrgId } = useAuth();
  const [results, setResults] = useState({ orgId: null, markets: [], partners: [] });
  const markets = results.orgId === activeOrgId ? results.markets : [];
  const partners = results.orgId === activeOrgId ? results.partners : [];

  // Global ⌘K / Ctrl+K shortcut.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);

  // Reload when the selected organization changes; never retain client results in Demo.
  useEffect(() => {
    if (!open || !activeOrgId) return;
    let ignore = false;
    Promise.all([
      api.get("/market-data/markets", { headers: { "X-Organization-ID": activeOrgId } })
        .then(({ data }) => (data?.markets || []).map((m) => ({ key: m.key, label: localizeLabel(m.origin, m.destination) })))
        .catch(() => []),
      api.get(`/organizations/${activeOrgId}/partners`)
        .then(({ data }) => (data?.active || []).map((p) => ({ id: p.id, name: p.partner?.name, type: p.partner?.type })).filter((p) => p.name))
        .catch(() => []),
    ]).then(([markets, partners]) => {
      if (!ignore) setResults({ orgId: activeOrgId, markets, partners });
    });
    return () => { ignore = true; };
  }, [open, activeOrgId]);

  const go = useCallback((path) => {
    setOpen(false);
    navigate(path);
  }, [navigate, setOpen]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput data-testid="command-palette-input" placeholder="Rechercher un marché, un partenaire, une page…" />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>Aucun résultat.</CommandEmpty>

        <CommandGroup heading="Navigation">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.id}
                data-testid={`command-nav-${item.id}`}
                value={`page ${item.label} ${item.id}`}
                onSelect={() => go(item.path)}
              >
                <Icon size={16} className="mr-2.5 text-lodge-ardoise" />
                <span>{item.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>

        {markets.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Marchés">
              {markets.map((m) => (
                <CommandItem
                  key={m.key}
                  data-testid={`command-market-${m.key}`}
                  value={`marché market ${m.label}`}
                  onSelect={() => go(`/markets/${m.key}`)}
                >
                  <Globe size={16} className="mr-2.5 text-lodge-deep" />
                  <span className="flex-1">{m.label}</span>
                  <ArrowUpRight size={13} className="text-lodge-ardoise-soft" />
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        {partners.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Partenaires">
              {partners.map((p) => (
                <CommandItem
                  key={p.id}
                  data-testid={`command-partner-${p.id}`}
                  value={`partenaire partner ${p.name}`}
                  onSelect={() => go("/partners")}
                >
                  <Users size={16} className="mr-2.5 text-lodge-deep" />
                  <span className="flex-1">{p.name}</span>
                  {p.type && <span className="text-[11px] text-lodge-ardoise-soft">{p.type}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}

        <CommandSeparator />
        <CommandGroup heading="Raccourcis">
          <CommandItem data-testid="command-shortcut-markets" value="explorer marchés explore markets" onSelect={() => go("/markets")}>
            <Compass size={16} className="mr-2.5 text-lodge-ardoise" />
            <span>Explorer tous les marchés</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};
